import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Button Variant Guidelines (matching Fortuna Softec design):
 *
 * GRADIENT VARIANTS (Primary actions with visual emphasis):
 * - default: Primary CTA, form submissions, main actions
 * - destructive: Delete, remove, dangerous actions
 * - secondary: Secondary actions that need some emphasis
 *
 * FLAT VARIANTS (Auxiliary/supporting actions):
 * - outline: Cancel buttons, secondary options, less prominent actions
 * - ghost: Toolbar buttons, icon-only actions, inline actions
 * - link: Text links, inline navigation
 *
 * ── Action color roles (Welle 3.1 der UX-Entlastung, siehe --action-* in
 * index.css) ────────────────────────────────────────────────────────────
 * Genau EIN primärer (gefüllter) Button pro Sichtbereich:
 *   action-primary     → variant="default"      (Blau — die eine Haupt-CTA)
 *   action-secondary   → variant="outline"      (neutral, Rest der Aktionen)
 *   action-tertiary    → variant="ghost"         (neutral, am wenigsten Gewicht)
 *   action-destructive → variant="destructive"   (Rot — exklusiv für Löschen/Verwerfen)
 * Grün und Orange sind ab hier ausschließlich Status-Farben (Badges, Ampeln,
 * Toasts) — NIE eine Button-Aktionsfarbe. Ein aktiver Umschalter/Modus-Button
 * bekommt dafür mehr *Gewicht* (secondary statt outline), keine Statusfarbe.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // Primary action buttons - gradient styling with consistent effects
        default:
          "bg-gradient-to-b from-primary to-primary/90 text-primary-foreground shadow-md hover:shadow-lg hover:from-primary/95 hover:to-primary/85 active:shadow-sm active:translate-y-[1px] transition-all duration-150",
        destructive:
          "bg-gradient-to-b from-destructive to-destructive/90 text-destructive-foreground shadow-md hover:shadow-lg hover:from-destructive/95 hover:to-destructive/85 active:shadow-sm active:translate-y-[1px] transition-all duration-150",
        secondary:
          "bg-gradient-to-b from-secondary to-secondary/90 text-secondary-foreground shadow-sm border border-border/50 hover:shadow-md hover:from-secondary/95 hover:to-secondary/85 active:shadow-xs active:translate-y-[1px] transition-all duration-150",
        // Auxiliary buttons - flat styling
        outline:
          "border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground hover:shadow-md active:shadow-sm active:translate-y-[1px] transition-all duration-150",
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-150",
        link: "text-primary underline-offset-4 hover:underline transition-colors duration-150",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 gap-1.5",
        lg: "h-11 rounded-md px-6",
        icon: "h-10 w-10",
        xs: "h-8 min-w-8 rounded-md gap-1 px-2 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Shows a loading spinner and disables the button */
  loading?: boolean
  /** Text to show while loading (replaces children) */
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, loadingText, children, disabled, ...props }, ref) => {
    const Comp = asChild && !loading ? Slot : "button"
    const isDisabled = disabled || loading

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loading && "cursor-wait"
        )}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading ? "true" : undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            <span>{loadingText || children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
